import DatePicker, { DatePickerProps } from '@/shared/components/DatePicker'
import { SizeVariants } from '@/shared/forms/components/constants'
import React, { ReactNode } from 'react'
import clsx from 'clsx'
import { formatDate, parseDate } from '@/lib/utils'
import { useFormContext } from 'react-hook-form'
import { Matcher } from 'react-day-picker'

type DatePickerRequiredFields = {
  name: string
  label: string
  size?: keyof typeof SizeVariants
  disabled?: boolean
  placeholder?: string
  variant?: 'day-only' | 'month-and-year'

  // When issuing medical certificate, doctors need to be able to reset the date to null.
  // This can be used for that.
  contentAfterLabel?: ReactNode
} & Omit<DatePickerProps, 'onTouch'>

export default function RHFDatePicker({
  name,
  label,
  size = 'full',
  inputIsDisabled = false,
  variant = 'day-only',
  contentAfterLabel,
  ...props
}: DatePickerRequiredFields) {
  const { formState, setValue, watch, trigger } = useFormContext()
  const { isSubmitting, touchedFields, dirtyFields, errors } = formState
  const isDisabled = inputIsDisabled || isSubmitting
  const isTouched = touchedFields?.[name] || dirtyFields?.[name]
  const error = errors[name]
  const value = watch(name)
  const selectedDateString = value ? parseDate(value) : undefined

  const onSelect = async (_day: Date | undefined, selectedDay: Date) => {
    const dateString = formatDate(selectedDay)
    setValue(name, dateString, {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    })
    await trigger(name)
  }

  return (
    <div className={SizeVariants[size as keyof typeof SizeVariants]}>
      <label
        htmlFor={props.id}
        className={clsx(
          'block text-sm font-medium leading-6',
          isDisabled ? 'text-gray-500' : 'text-gray-900',
        )}
      >
        {label}
        {contentAfterLabel}
      </label>
      <DatePicker
        captionLayout={variant === 'day-only' ? 'buttons' : 'dropdown-buttons'}
        error={isTouched && error ? `${error?.message}` : undefined}
        selected={selectedDateString}
        inputIsDisabled={isDisabled}
        onSelect={onSelect}
        defaultMonth={selectedDateString}
        onTouch={() => setValue(name, value, { shouldTouch: true })}
        variant={variant}
        {...props}
      ></DatePicker>
      {isTouched && error && (
        <div className="mt-2 text-sm font-semibold text-red-600">
          {`${error?.message}`}
        </div>
      )}
    </div>
  )
}
